import { useState } from 'react';
import { Box } from '@mui/material';
import { useSnackbar } from '../../../../Context/SnackbarContext';
import { AdviseCurrentSupplier } from '../../../../types/advise';
import { useEngagementEditor } from './EngagementEditorContext';
import { CurrentSuppliersEmptyState } from './CurrentSuppliersEmptyState';
import { AddCurrentSupplierModal } from './AddCurrentSupplierModal';
import { InvoiceView } from './InvoiceView';
import { mostRecentInvoice } from './InvoiceView.calc';
import { PanelHeader } from './PanelHeader';
import { CurrentSuppliersSummary } from './CurrentSuppliersSummary';
import { CurrentSupplierCards, OpenSupplierEntry } from './CurrentSupplierCards';

// A parsed invoice offers no monthly total to edit, so the card's Edit beside one opens the total itself.
const openedInvoice = (supplier: AdviseCurrentSupplier, invoiceId: string | null) => {
  if (invoiceId) return supplier.invoices.find((invoice) => invoice.id === invoiceId) ?? null;
  const recent = mostRecentInvoice(supplier);
  return supplier.spend_entry === 'monthly_total' && recent?.status === 'parsed' ? null : recent;
};

const MANUAL_ENTRY_UNOPENED = "The supplier was added, but couldn't be opened for manual entry.";

/** The Current Suppliers step: its suppliers' cards, or the empty state with the $0 baseline skip. */
export const CurrentSuppliersPanel = () => {
  const { engagement, canEdit, currentSuppliers } = useEngagementEditor();
  const current = engagement.current_suppliers ?? { skipped: false, suppliers: [] };
  const suppliers = current.suppliers ?? [];
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState<OpenSupplierEntry | null>(null);
  const { setSnack } = useSnackbar();
  const openSupplier = open && suppliers.find((supplier) => supplier.id === open.supplierId);
  const openInvoice = openSupplier ? openedInvoice(openSupplier, open.invoiceId) : null;
  const invoiceGone = !!open?.invoiceId && !openInvoice;

  if (openSupplier && !invoiceGone) {
    return (
      <InvoiceView
        supplier={openSupplier}
        invoice={openInvoice}
        onBack={() => setOpen(null)}
      />
    );
  }

  return (
    <Box className="flex flex-col gap-3">
      <PanelHeader
        className="border-b border-grey-2 pb-4"
        title="Current Suppliers & Spend"
        description="Add the client's current suppliers and upload a recent invoice for each so line items can be parsed — or enter spend manually. In-scope monthly spend carries into ROI Calc. as the client's current cost."
      />
      <CurrentSuppliersSummary suppliers={suppliers} />
      {suppliers.length === 0 && (
        <CurrentSuppliersEmptyState
          skipped={current.skipped}
          canEdit={canEdit}
          onAdd={() => setAdding(true)}
          onSkip={(skipped) => currentSuppliers?.skip(skipped)}
        />
      )}
      <CurrentSupplierCards
        suppliers={suppliers}
        onOpen={setOpen}
        onAdd={() => setAdding(true)}
      />
      {adding && (
        <AddCurrentSupplierModal
          onClose={() => setAdding(false)}
          onAdded={(supplierId, openManualEntry) => {
            setAdding(false);
            if (!openManualEntry) return;
            if (supplierId) setOpen({ supplierId, invoiceId: null });
            else setSnack({ message: MANUAL_ENTRY_UNOPENED, open: true, type: 'warning' });
          }}
        />
      )}
    </Box>
  );
};
